import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { IconClose } from "./SiteIcons";
import { MQ } from "./WebsiteTheme.style";

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 2000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 32px;
	background: rgba(8, 18, 54, 0.82);
	font-family: "Outfit", sans-serif;

	${MQ.PHONE} {
		padding: 16px;
	}

	.svm_dialog {
		position: relative;
		width: 100%;
		max-width: 1040px;
	}

	video {
		display: block;
		width: 100%;
		aspect-ratio: 16 / 9;
		max-height: calc(100vh - 140px);
		border-radius: 16px;
		background: #000;
	}

	.svm_close {
		position: absolute;
		top: -52px;
		right: 0;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		min-height: 44px;
		padding: 8px 14px;
		border: 0;
		border-radius: 10px;
		background: rgba(255, 255, 255, 0.14);
		color: #fff;
		font-family: inherit;
		font-size: 15px;
		font-weight: 600;
		cursor: pointer;

		svg {
			width: 20px;
			height: 20px;
		}

		&:hover {
			background: rgba(255, 255, 255, 0.24);
		}

		&:focus-visible {
			outline: 3px solid #8fb5ff;
			outline-offset: 2px;
		}
	}
`;

/**
 * Plays a video in a dialog over the page. Closes on Escape, on the close button and on a click
 * outside the video; page scrolling is paused while it is open and focus returns to the opener.
 */
const SiteVideoModal = ({ open, onClose, src, title }) => {
	const closeRef = useRef(null);

	useEffect(() => {
		if (!open) return undefined;
		const opener = document.activeElement;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		if (closeRef.current) closeRef.current.focus();

		const onKey = (event) => event.key === "Escape" && onClose();
		window.addEventListener("keydown", onKey);
		return () => {
			window.removeEventListener("keydown", onKey);
			document.body.style.overflow = previousOverflow;
			if (opener && opener.focus) opener.focus();
		};
	}, [open, onClose]);

	if (!open) return null;

	return ReactDOM.createPortal(
		<Overlay onClick={(event) => event.target === event.currentTarget && onClose()}>
			<div className="svm_dialog" role="dialog" aria-modal="true" aria-label={title}>
				<button ref={closeRef} type="button" className="svm_close" onClick={onClose}>
					<IconClose />
					<span>Close</span>
				</button>
				<video src={src} controls autoPlay playsInline>
					Your browser does not support the video tag.
				</video>
			</div>
		</Overlay>,
		document.body
	);
};

export default SiteVideoModal;
